<script setup lang="ts">
  import { CalendarDate } from '@internationalized/date';
  import type { DateValue } from '@internationalized/date';
  import dayjs from 'dayjs';
  import { ref, watch } from 'vue';

  import type { Invoice } from '@stuut/api/models';
  import type { HttpError } from '@stuut/common/src/api_client';
  import { Button } from '@ui/components/button';
  import { Calendar } from '@ui/components/calendar';
  import { Popover, PopoverContent, PopoverTrigger } from '@ui/components/popover';
  import { toast } from '@ui/components/sonner';

  import { stuut } from '@/api';

  const props = defineProps<{
    invoice: Invoice;
    saveOnClear?: boolean;
  }>();

  const emit = defineEmits<{
    (e: 'saved', invoice: Invoice): void;
  }>();

  const toCalendarDate = (dayjsDate: dayjs.Dayjs): CalendarDate =>
    new CalendarDate(dayjsDate.year(), dayjsDate.month() + 1, dayjsDate.date());

  const isOpen = ref(false);
  const isSaving = ref(false);
  const promiseToPayDate = ref<DateValue | undefined>(
    props.invoice?.promised_to_pay_date
      ? toCalendarDate(dayjs(props.invoice.promised_to_pay_date))
      : undefined,
  );

  watch(isOpen, () => {
    promiseToPayDate.value = props.invoice?.promised_to_pay_date
      ? toCalendarDate(dayjs(props.invoice.promised_to_pay_date))
      : undefined;
  });

  const clear = () => {
    promiseToPayDate.value = undefined;
    if (props.saveOnClear) save();
  };

  const save = async () => {
    isSaving.value = true;
    try {
      await stuut.invoices.update(
        props.invoice.id,
        {
          data: {
            promised_to_pay_date: promiseToPayDate.value?.toString() || null,
          },
        },
        props.invoice,
      );

      isOpen.value = false;
      toast.success('Updated promised payment date');
      emit('saved', props.invoice);
    } catch (err) {
      toast.error((err as HttpError).message || 'An error occurred.');
    } finally {
      isSaving.value = false;
    }
  };
</script>

<template>
  <Popover v-model:open="isOpen">
    <PopoverTrigger as-child>
      <slot :promised-to-pay-date="invoice.promised_to_pay_date" />
    </PopoverTrigger>

    <PopoverContent class="w-auto p-3 space-y-3">
      <!-- @vue-ignore -->
      <Calendar v-model="promiseToPayDate" calendar-label="Promise To Pay" />

      <div class="flex flex-row gap-x-2">
        <Button
          variant="outline"
          size="sm"
          class="flex-1"
          :loading="saveOnClear && isSaving"
          :disabled="isSaving"
          @click="clear"
        >
          Clear
        </Button>
        <Button variant="primary" size="sm" class="flex-1" :loading="isSaving" @click="save">
          Save
        </Button>
      </div>
    </PopoverContent>
  </Popover>
</template>
